import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { ColDef } from 'ag-grid-community';
import { ToastrService } from 'ngx-toastr';
import { SevaProfileService } from 'src/app/Service/seva-profile.service';

export interface AuditHistoryConfig {
  apiUrl: string; // API endpoint with query parameters (e.g., '/AuditHistory/GetAuditHistoryODataAsync?sevaProfileUID=...')
  columnDefs?: ColDef[]; // Optional custom column definitions
}

@Component({
  selector: 'app-audit-history-grid',
  templateUrl: './audit-history-grid.component.html',
  styleUrls: ['./audit-history-grid.component.scss']
})
export class AuditHistoryGridComponent implements OnInit, OnDestroy, OnChanges {
  @Input() config!: AuditHistoryConfig;

  themeClass = 'ag-theme-quartz';
  paginationPageSize = 10;
  paginationPageSizeSelector = [10, 25, 50, 100];
  gridOptions = { pagination: true, paginationPageSize: this.paginationPageSize };
  public defaultColDef: ColDef = { 
    filter: "agTextColumnFilter", 
    floatingFilter: true, 
    sortable: true,
    resizable: true
  };
  
  currentPage = 1;
  totalPages = 1;
  gridData: any[] = [];
  totalItems = 0;
  isLoading = false;

  // Default column definitions for audit history
  private defaultColumnDefs: ColDef[] = [
    { 
      headerName: 'Action', 
      field: 'Action', 
      flex: 1, 
      minWidth: 100, 
      sortable: true, 
      filter: true 
    },
    { 
      headerName: 'Action Message', 
      field: 'ActionMessage', 
      flex: 2, 
      minWidth: 200, 
      sortable: true, 
      filter: true 
    },
    { 
      headerName: 'Table Name', 
      field: 'TableName', 
      flex: 1, 
      minWidth: 150, 
      sortable: true, 
      filter: true 
    },
    { 
      headerName: 'Action Date', 
      field: 'ActionDate', 
      flex: 1, 
      minWidth: 180, 
      sortable: true, 
      filter: true,
      cellRenderer: (params: any) => {
        if (params.value) {
          const date = new Date(params.value);
          return date.toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          });
        }
        return '';
      }
    },
    { 
      headerName: 'Created By', 
      field: 'CreatedBy', 
      flex: 1, 
      minWidth: 120, 
      sortable: true, 
      filter: true 
    },
    { 
      headerName: 'Seva Profile UID', 
      field: 'SevaProfileDisplayUID', 
      flex: 1, 
      minWidth: 150, 
      sortable: true, 
      filter: true 
    }
  ];

  get displayedColumnDefs(): ColDef[] {
    return this.config?.columnDefs && this.config.columnDefs.length > 0 
      ? this.config.columnDefs 
      : this.defaultColumnDefs;
  }

  constructor(
    private api: SevaProfileService,
    private toastr: ToastrService,
    private cd: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    if (this.config && this.config.apiUrl) {
      this.loadAuditHistory(1);
    } else {
      console.warn('AuditHistoryGridComponent: config or apiUrl not provided');
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config'] && this.config && this.config.apiUrl) {
      // Reset to first page when config changes
      this.currentPage = 1;
      this.loadAuditHistory(1);
    }
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }

  /**
   * Load audit history data from API
   */
  loadAuditHistory(page: number): void {
    if (!this.config || !this.config.apiUrl) {
      console.error('AuditHistoryGridComponent: config or apiUrl not provided');
      return;
    }

    this.isLoading = true;
    const validPage = Math.max(1, page);
    this.currentPage = validPage;

    // Build API endpoint with pagination
    // OData endpoints typically use $skip and $top for pagination
    const skip = (validPage - 1) * this.paginationPageSize;
    const top = this.paginationPageSize;
    
    // Check if endpoint already has query parameters
    const separator = this.config.apiUrl.includes('?') ? '&' : '?';
    const endpoint = `${this.config.apiUrl}${separator}$skip=${skip}&$top=${top}`;

    this.api.apiCall(endpoint, 'GET')
      .subscribe({
        next: (response: any) => {
          this.isLoading = false;
          
          if (response && response.Data) {
            // Handle OData response structure
            this.gridData = response.Data.value || [];
            
            // Get total count from OData response
            const totalCount = response.Data['@odata.count'] || 
                             response.Data['@odata.totalCount'] || 
                             response.Data['@odata.filteredCount'] || 
                             0;
            
            this.totalItems = totalCount;
            this.totalPages = Math.ceil(totalCount / this.paginationPageSize) || 1;
            
            if (this.totalPages === 0 && totalCount > 0) {
              this.totalPages = 1;
            }
            
            this.cd.detectChanges();
          } else {
            console.error('Invalid API response structure:', response);
            this.toastr.error('Failed to load audit history');
            this.gridData = [];
          }
        },
        error: (error) => {
          this.isLoading = false;
          console.error('API call failed:', error);
          this.toastr.error('Failed to load audit history');
          this.gridData = [];
          this.cd.detectChanges();
        }
      });
  }

  // Pagination methods
  goToFirstPage(): void {
    if (this.currentPage !== 1) {
      this.loadAuditHistory(1);
    }
  }

  goToPreviousPage(): void {
    if (this.currentPage > 1) {
      this.loadAuditHistory(this.currentPage - 1);
    }
  }

  goToNextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.loadAuditHistory(this.currentPage + 1);
    }
  }

  goToLastPage(): void {
    if (this.currentPage !== this.totalPages) {
      this.loadAuditHistory(this.totalPages);
    }
  }

  onPageSizeChange(): void {
    this.currentPage = 1;
    this.gridOptions.paginationPageSize = this.paginationPageSize;
    this.loadAuditHistory(1);
  }

  onGridReady(params: any): void {
    params.api.sizeColumnsToFit();
  }

  /**
   * Refresh the audit history data
   */
  refresh(): void {
    this.loadAuditHistory(this.currentPage);
  }
}
